// Icons
import { Workflow, Hotel, Building2, Search } from "lucide-react"

// ---------------------------------------------------------------------------
// The live flow diagram - a top-down map of the open (or preview-hovered)
// flow, drawn on the dot-grid canvas. A client enquiry feeds into the
// starting hotel, then a rail branches down to each alternative we'd quote
// alongside it. While the agent is mid-search in the builder, a dashed ghost
// node sketches where their pick will land.
// ---------------------------------------------------------------------------
export default function FlowDiagram({ flow, sketch, isPreview }) {
	// Nothing open and nothing hovered - point at the panel
	if (!flow) {
		return (
			<div className="qa-diagram-empty">
				<div className="qa-diagram-empty-icon">
					<Workflow size={22} />
				</div>
				<h3>Nothing to map yet</h3>
				<p>Open a flow, or start a new one - the diagram draws itself here as you make your choices.</p>
			</div>
		)
	}

	const alternatives = flow.alternatives || []

	// Ghost nodes - what's being typed into a picker right now
	const ghostStart = !flow.start && sketch?.target === "start" ? sketch.text : null
	const ghostAlternative = flow.start && sketch?.target === "alternative" ? sketch.text : null

	const showBranches = alternatives.length > 0 || ghostAlternative

	return (
		<div className="qa-diagram">
			{/* Hover-preview tag */}
			{isPreview && <span className="qa-diagram-preview">Previewing</span>}

			<div className="qa-diagram-flow">
				{/* The enquiry landing */}
				<div className="qa-node-enquiry">
					<span className="qa-pulse-dot" />
					Client enquiry
				</div>

				<div className="qa-rail-segment" />

				{/* The starting hotel - real, ghost, or still to be chosen */}
				{flow.start ? (
					<div className="qa-node is-start" key={flow.start.id}>
						<div className="qa-node-icon">
							<Hotel size={16} />
						</div>
						<div className="qa-node-copy">
							<small>Starting hotel</small>
							<p>{flow.start.title}</p>
						</div>
					</div>
				) : ghostStart ? (
					<div className="qa-node is-start is-ghost">
						<div className="qa-node-icon">
							<Search size={16} />
						</div>
						<div className="qa-node-copy">
							<small>Searching...</small>
							<p>{ghostStart}</p>
						</div>
					</div>
				) : (
					<div className="qa-node is-start is-pending">
						<div className="qa-node-icon">
							<Hotel size={16} />
						</div>
						<div className="qa-node-copy">
							<small>Starting hotel</small>
							<p>Waiting on a choice</p>
						</div>
					</div>
				)}

				{/* The alternatives, branching off a shared rail */}
				{showBranches && (
					<>
						<div className="qa-rail-segment is-labelled">
							<span>also quote</span>
						</div>

						<div className="qa-branches">
							{alternatives.map((alternative, index) => (
								<div key={alternative.id} className="qa-branch" style={{ animationDelay: `${index * 45}ms` }}>
									<div className="qa-node is-alternative">
										<div className="qa-node-icon">
											<Building2 size={16} />
										</div>
										<div className="qa-node-copy">
											<small>Alternative {index + 1}</small>
											<p>{alternative.title}</p>
										</div>
									</div>
								</div>
							))}

							{/* Ghost branch while the agent is mid-search */}
							{ghostAlternative && (
								<div className="qa-branch is-ghost-branch">
									<div className="qa-node is-alternative is-ghost">
										<div className="qa-node-icon">
											<Search size={16} />
										</div>
										<div className="qa-node-copy">
											<small>Searching...</small>
											<p>{ghostAlternative}</p>
										</div>
									</div>
								</div>
							)}
						</div>
					</>
				)}

				{/* A line under the flow once it's taking shape */}
				{flow.start && (
					<p className="qa-diagram-stats">
						{alternatives.length > 0
							? `An enquiry for ${flow.start.title} gets quoted with ${alternatives.length} alternative ${alternatives.length === 1 ? "hotel" : "hotels"}.`
							: "Add alternatives in the builder to grow this flow."}
					</p>
				)}
			</div>
		</div>
	)
}
